import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useWebSocketContext } from '@/context/WebSocketContext'
import {
  isOrderCreatedEvent,
  isNotificationReadEvent,
  isTransferSentEvent,
  isTransferReturnedEvent,
  isTransferCreatedFromOrderEvent,
  isTransferRespondedEvent,
  isTransferRequestedEvent,
  isTransferApprovedEvent,
  isTransferCancelledEvent,
  isSalesWindowNotificationEvent,
  isWarehouseDebtLimitConsumedEvent,
  isChatSessionCreatedEvent,
  isChatMessageCreatedEvent,
  isChatSessionClosedEvent,
} from '@/types/websocket'
import {
  isSalesWindowNotificationType,
  getSalesWindowNotificationText,
  salesWindowLink,
} from '@/lib/sales-window-notification'
import {
  getWarehouseDebtLimitNotificationText,
  warehouseDebtLimitLink,
} from '@/lib/warehouse-debt-limit-notification'
import { useUser } from '@/context/UserContext'
import { useGetMe } from '@/services/queries/user/use-get-me'
import { toast } from '@/components/ui/use-toast'
import { ToastAction } from '@/components/ui/toast'
import type { IOrderList } from '@/pages/orders/orders/types/order'
import { isSourceFilteredTransferListKey } from '@/pages/products-actions/product-transfer/lib/transfer-source'
import type { IProductActionList } from '@/types/product-actions'
import type { Paginated } from '@/types/utility.types'
import type { ApiNotification } from '@/types/notification'

// ── Audio ──────────────────────────────────────────────────────────────────────
type SoundType = 'order' | 'transfer'

// Derives the sound type from the WS event-type prefix:
//   order.*     → order  (synthesized ding via AudioContext)
//   transfer.*  → transfer (order.wav)
//   anything else → no sound
function resolveSound(eventType: string): SoundType | null {
  const prefix = eventType.split('.')[0]
  if (prefix === 'order') return 'order'
  if (prefix === 'transfer') return 'transfer'
  if (prefix === 'sales_window') return 'transfer'
  if (prefix === 'warehouse') return 'transfer'
  return null
}

// Singleton AudioContext — browsers block it until first user gesture
let _audioCtx: AudioContext | null = null

function getAudioCtx(): AudioContext | null {
  if (_audioCtx) return _audioCtx
  try {
    _audioCtx = new AudioContext()
    return _audioCtx
  } catch {
    return null
  }
}

function resumeAudioCtx() {
  const ctx = getAudioCtx()
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {})
}

if (typeof document !== 'undefined') {
  document.addEventListener('click', resumeAudioCtx)
  document.addEventListener('keydown', resumeAudioCtx)
}

function doPlayDing(ctx: AudioContext) {
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()
  oscillator.connect(gain)
  gain.connect(ctx.destination)
  oscillator.frequency.setValueAtTime(880, ctx.currentTime)
  oscillator.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.5)
  gain.gain.setValueAtTime(1.0, ctx.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8)
  oscillator.start(ctx.currentTime)
  oscillator.stop(ctx.currentTime + 0.8)
}

// transfer uses order.wav
let _transferAudio: HTMLAudioElement | null = null

function playSound(type: SoundType) {
  try {
    if (type === 'transfer') {
      if (!_transferAudio) _transferAudio = new Audio('/audio/order.wav')
      _transferAudio.currentTime = 0
      _transferAudio.play().catch(() => {})
    } else {
      const ctx = getAudioCtx()
      if (!ctx) return
      if (ctx.state === 'running') {
        doPlayDing(ctx)
      } else {
        ctx
          .resume()
          .then(() => doPlayDing(ctx))
          .catch(() => {})
      }
    }
  } catch {
    // fail silently
  }
}

function upsertTransferInCache(
  queryClient: QueryClient,
  transfer: IProductActionList
) {
  const queries = queryClient.getQueriesData<Paginated<IProductActionList>>({
    queryKey: ['product-transfer-list'],
    exact: false,
  })

  let found = false
  for (const [queryKey, data] of queries) {
    if (!data) continue
    if (!data.results.some((t) => t.id === transfer.id)) continue
    found = true
    queryClient.setQueryData<Paginated<IProductActionList>>(queryKey, {
      ...data,
      results: data.results.map((t) =>
        t.id === transfer.id ? { ...t, status: transfer.status } : t
      ),
    })
  }

  if (found) return

  for (const [queryKey, data] of queries) {
    if (!data) continue
    if (isSourceFilteredTransferListKey(queryKey)) continue
    const keyParams = queryKey[1] as Record<string, string | null>
    const page = keyParams?.page
    if (page !== null && page !== '1') continue
    const pageSize = Number(keyParams?.page_size ?? '10')
    queryClient.setQueryData<Paginated<IProductActionList>>(queryKey, {
      ...data,
      count: data.count + 1,
      results: [transfer, ...data.results].slice(0, pageSize),
    })
    break
  }
}

export function useNotificationListener() {
  const { lastMessage } = useWebSocketContext()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation('global')
  const { user } = useUser()
  const { data: me } = useGetMe()
  const prevRef = useRef(lastMessage)

  useEffect(() => {
    if (!lastMessage || lastMessage === prevRef.current) return
    prevRef.current = lastMessage

    // ── order.created ──────────────────────────────────────────────────────────
    if (isOrderCreatedEvent(lastMessage)) {
      const {
        id,
        order_number,
        status,
        total_price,
        payment_method,
        delivery_method,
        user: wsUser,
        target_admin_ids,
      } = lastMessage.data

      // Only process events directed at this admin.
      // Fail open while user data is still loading so no notification is missed.
      if (user?.id !== undefined && !target_admin_ids.includes(user.id)) return

      if (me?.notifications_enabled !== false) {
        const sound = resolveSound(lastMessage.type)
        if (sound) playSound(sound)

        const title = t('notifications.order_created.title')
        const message = `${order_number} — ${total_price}`
        const link = `/orders/${id}`
        const viewLabel = t('notifications.view')

        toast({
          variant: 'info',
          title,
          description: message,
          duration: 6000,
          action: (
            <ToastAction altText={viewLabel} onClick={() => navigate(link)}>
              {viewLabel}
            </ToastAction>
          ),
        })
      }

      // ── Optimistic notifications cache update ──────────────────────────────
      const optimisticNotif: ApiNotification = {
        id: -Date.now(), // temporary negative id, overwritten on refetch
        object_id: id,
        notification_type: 'new_order',
        is_read: false,
        read_by: null,
        warehouse: 0,
        created_datetime: lastMessage.timestamp,
      }

      queryClient.setQueryData<Paginated<ApiNotification>>(
        ['notifications'],
        (old) => {
          if (!old) return old
          // Deduplication guard
          if (old.results.some((n) => n.object_id === id && n.id > 0))
            return old
          return {
            ...old,
            count: old.count + 1,
            results: [optimisticNotif, ...old.results].slice(0, 20),
          }
        }
      )

      // ── Optimistic orders cache update ─────────────────────────────────────
      const newOrder: IOrderList = {
        id,
        status,
        payment_method,
        delivery_method,
        total_price,
        created_datetime: new Date().toISOString(),
        client_comment: null,
        warehouse: {
          id: 0,
          name_uz: '',
          name_ru: '',
          name_en: '',
        },
        user: {
          id: 0,
          first_name: wsUser.first_name,
          last_name: '',
          username: wsUser.username,
          role: '',
        },
      }

      const queries = queryClient.getQueriesData<Paginated<IOrderList>>({
        queryKey: ['order-list'],
        exact: false,
      })

      for (const [queryKey, oldData] of queries) {
        if (!oldData) continue

        const keyParams = new URLSearchParams(queryKey[1] as string)
        const page = Number(keyParams.get('page') ?? '1')
        if (page !== 1) continue

        if (oldData.results.some((o) => o.id === newOrder.id)) continue

        const pageSize = Number(keyParams.get('page_size') ?? '10')
        queryClient.setQueryData<Paginated<IOrderList>>(queryKey, {
          ...oldData,
          count: oldData.count + 1,
          results: [newOrder, ...oldData.results].slice(0, pageSize),
        })
      }

      return
    }

    // ── notification.read ──────────────────────────────────────────────────────
    if (isNotificationReadEvent(lastMessage)) {
      const { notification_id } = lastMessage.data

      queryClient.setQueryData<Paginated<ApiNotification>>(
        ['notifications'],
        (old) => {
          if (!old) return old
          return {
            ...old,
            count: Math.max(0, old.count - 1),
            results: old.results.filter((n) => n.id !== notification_id),
          }
        }
      )

      return
    }

    // ── transfer.sent ──────────────────────────────────────────────────────────
    if (isTransferSentEvent(lastMessage)) {
      const {
        id,
        user: wsUser,
        from_warehouse,
        to_warehouse,
        status,
        total_price,
        comment,
        target_admin_ids,
      } = lastMessage.data

      if (user?.id !== undefined && !target_admin_ids.includes(user.id)) return

      if (me?.notifications_enabled !== false) {
        const sound = resolveSound(lastMessage.type)
        if (sound) playSound(sound)

        const viewLabel = t('notifications.view')
        toast({
          variant: 'info',
          title: t('notifications.transfer_sent.title'),
          description: `#${id} — ${from_warehouse} → ${to_warehouse}`,
          duration: 6000,
          action: (
            <ToastAction
              altText={viewLabel}
              onClick={() =>
                navigate(`/products-actions/product-transfer/${id}`)
              }
            >
              {viewLabel}
            </ToastAction>
          ),
        })
      }

      queryClient.setQueryData<Paginated<ApiNotification>>(
        ['notifications'],
        (old) => {
          if (!old) return old
          if (
            old.results.some(
              (n) =>
                n.object_id === id &&
                n.notification_type === 'transfer_sent' &&
                n.id > 0
            )
          )
            return old
          return {
            ...old,
            count: old.count + 1,
            results: [
              {
                id: -Date.now(),
                object_id: id,
                notification_type: 'transfer_sent',
                is_read: false,
                read_by: null,
                warehouse: 0,
                created_datetime: lastMessage.timestamp,
              },
              ...old.results,
            ].slice(0, 20),
          }
        }
      )

      upsertTransferInCache(queryClient, {
        id,
        status,
        comment,
        document_price: total_price,
        from_warehouse: { id: 0, name: from_warehouse },
        to_warehouse: { id: 0, name: to_warehouse },
        transfer_items: [],
        created_datetime: lastMessage.timestamp,
        modified_datetime: lastMessage.timestamp,
        requested_at: null,
        sent_at: lastMessage.timestamp,
        received_at: null,
        order: null,
        order_payment_method: null,
        executor: {
          id: 0,
          first_name: '',
          last_name: '',
          username: wsUser.username,
        },
      })
      return
    }

    // ── transfer.returned ──────────────────────────────────────────────────────
    if (isTransferReturnedEvent(lastMessage)) {
      const {
        id,
        user: wsUser,
        from_warehouse,
        to_warehouse,
        status,
        total_price,
        comment,
        target_admin_ids,
      } = lastMessage.data

      if (user?.id !== undefined && !target_admin_ids.includes(user.id)) return

      if (me?.notifications_enabled !== false) {
        const sound = resolveSound(lastMessage.type)
        if (sound) playSound(sound)

        const viewLabel = t('notifications.view')
        toast({
          variant: 'info',
          title: t('notifications.transfer_returned.title'),
          description: `#${id} — ${from_warehouse} → ${to_warehouse}`,
          duration: 6000,
          action: (
            <ToastAction
              altText={viewLabel}
              onClick={() =>
                navigate(`/products-actions/product-transfer/${id}`)
              }
            >
              {viewLabel}
            </ToastAction>
          ),
        })
      }

      queryClient.setQueryData<Paginated<ApiNotification>>(
        ['notifications'],
        (old) => {
          if (!old) return old
          if (
            old.results.some(
              (n) =>
                n.object_id === id &&
                n.notification_type === 'transfer_returned' &&
                n.id > 0
            )
          )
            return old
          return {
            ...old,
            count: old.count + 1,
            results: [
              {
                id: -Date.now(),
                object_id: id,
                notification_type: 'transfer_returned',
                is_read: false,
                read_by: null,
                warehouse: 0,
                created_datetime: lastMessage.timestamp,
              },
              ...old.results,
            ].slice(0, 20),
          }
        }
      )

      upsertTransferInCache(queryClient, {
        id,
        status,
        comment,
        document_price: total_price,
        from_warehouse: { id: 0, name: from_warehouse },
        to_warehouse: { id: 0, name: to_warehouse },
        transfer_items: [],
        created_datetime: lastMessage.timestamp,
        modified_datetime: lastMessage.timestamp,
        requested_at: null,
        sent_at: null,
        received_at: null,
        order: null,
        order_payment_method: null,
        executor: {
          id: 0,
          first_name: '',
          last_name: '',
          username: wsUser.username,
        },
      })
      return
    }

    // ── transfer.created_from_order ────────────────────────────────────────────
    if (isTransferCreatedFromOrderEvent(lastMessage)) {
      const {
        id,
        user: wsUser,
        status,
        from_warehouse,
        to_warehouse,
        total_price,
        comment,
        target_admin_ids,
      } = lastMessage.data

      if (user?.id !== undefined && !target_admin_ids.includes(user.id)) return

      if (me?.notifications_enabled !== false) {
        const sound = resolveSound(lastMessage.type)
        if (sound) playSound(sound)

        const viewLabel = t('notifications.view')
        toast({
          variant: 'info',
          title: t('notifications.transfer_created_from_order.title'),
          description: `#${id} — ${from_warehouse} → ${to_warehouse}`,
          duration: 6000,
          action: (
            <ToastAction
              altText={viewLabel}
              onClick={() =>
                navigate(`/products-actions/product-transfer/${id}`)
              }
            >
              {viewLabel}
            </ToastAction>
          ),
        })
      }

      queryClient.setQueryData<Paginated<ApiNotification>>(
        ['notifications'],
        (old) => {
          if (!old) return old
          if (
            old.results.some(
              (n) =>
                n.object_id === id &&
                n.notification_type === 'transfer_created_from_order' &&
                n.id > 0
            )
          )
            return old
          return {
            ...old,
            count: old.count + 1,
            results: [
              {
                id: -Date.now(),
                object_id: id,
                notification_type: 'transfer_created_from_order',
                is_read: false,
                read_by: null,
                warehouse: 0,
                created_datetime: lastMessage.timestamp,
              },
              ...old.results,
            ].slice(0, 20),
          }
        }
      )

      upsertTransferInCache(queryClient, {
        id,
        status,
        comment,
        document_price: total_price,
        from_warehouse: { id: 0, name: from_warehouse },
        to_warehouse: { id: 0, name: to_warehouse },
        transfer_items: [],
        created_datetime: lastMessage.timestamp,
        modified_datetime: lastMessage.timestamp,
        requested_at: null,
        sent_at: null,
        received_at: null,
        order: null,
        order_payment_method: null,
        executor: {
          id: 0,
          first_name: '',
          last_name: '',
          username: wsUser.username,
        },
      })
      return
    }

    // ── transfer.responded ────────────────────────────────────────────────────
    if (isTransferRespondedEvent(lastMessage)) {
      const {
        id,
        user: wsUser,
        from_warehouse,
        to_warehouse,
        status,
        total_price,
        comment,
        target_admin_ids,
      } = lastMessage.data

      if (user?.id !== undefined && !target_admin_ids.includes(user.id)) return

      if (me?.notifications_enabled !== false) {
        const sound = resolveSound(lastMessage.type)
        if (sound) playSound(sound)

        const viewLabel = t('notifications.view')
        toast({
          variant: 'info',
          title: t('notifications.transfer_responded.title'),
          description: `#${id} — ${from_warehouse} → ${to_warehouse}`,
          duration: 6000,
          action: (
            <ToastAction
              altText={viewLabel}
              onClick={() =>
                navigate(`/products-actions/product-transfer/${id}`)
              }
            >
              {viewLabel}
            </ToastAction>
          ),
        })
      }

      queryClient.setQueryData<Paginated<ApiNotification>>(
        ['notifications'],
        (old) => {
          if (!old) return old
          if (
            old.results.some(
              (n) =>
                n.object_id === id &&
                n.notification_type === 'new_transfer' &&
                n.id > 0
            )
          )
            return old
          return {
            ...old,
            count: old.count + 1,
            results: [
              {
                id: -Date.now(),
                object_id: id,
                // Backend's upsert_for_transfer writes `new_transfer` for the
                // responded case — match it so the refetch dedups correctly.
                notification_type: 'new_transfer',
                is_read: false,
                read_by: null,
                warehouse: 0,
                created_datetime: lastMessage.timestamp,
              },
              ...old.results,
            ].slice(0, 20),
          }
        }
      )

      upsertTransferInCache(queryClient, {
        id,
        status,
        comment,
        document_price: total_price,
        from_warehouse: { id: 0, name: from_warehouse },
        to_warehouse: { id: 0, name: to_warehouse },
        transfer_items: [],
        created_datetime: lastMessage.timestamp,
        modified_datetime: lastMessage.timestamp,
        requested_at: null,
        sent_at: null,
        received_at: null,
        order: null,
        order_payment_method: null,
        executor: {
          id: 0,
          first_name: '',
          last_name: '',
          username: wsUser.username,
        },
      })
      return
    }

    // ── transfer.requested ────────────────────────────────────────────────────
    if (isTransferRequestedEvent(lastMessage)) {
      const {
        id,
        user: wsUser,
        from_warehouse,
        to_warehouse,
        status,
        total_price,
        comment,
        target_admin_ids,
      } = lastMessage.data

      if (user?.id !== undefined && !target_admin_ids.includes(user.id)) return

      if (me?.notifications_enabled !== false) {
        const sound = resolveSound(lastMessage.type)
        if (sound) playSound(sound)

        const viewLabel = t('notifications.view')
        toast({
          variant: 'info',
          title: t('notifications.transfer_requested.title'),
          description: `#${id} — ${from_warehouse} → ${to_warehouse}`,
          duration: 6000,
          action: (
            <ToastAction
              altText={viewLabel}
              onClick={() =>
                navigate(`/products-actions/product-transfer/${id}`)
              }
            >
              {viewLabel}
            </ToastAction>
          ),
        })
      }

      queryClient.setQueryData<Paginated<ApiNotification>>(
        ['notifications'],
        (old) => {
          if (!old) return old
          if (
            old.results.some(
              (n) =>
                n.object_id === id &&
                n.notification_type === 'transfer_requested' &&
                n.id > 0
            )
          )
            return old
          return {
            ...old,
            count: old.count + 1,
            results: [
              {
                id: -Date.now(),
                object_id: id,
                notification_type: 'transfer_requested',
                is_read: false,
                read_by: null,
                warehouse: 0,
                created_datetime: lastMessage.timestamp,
              },
              ...old.results,
            ].slice(0, 20),
          }
        }
      )

      upsertTransferInCache(queryClient, {
        id,
        status,
        comment,
        document_price: total_price,
        from_warehouse: { id: 0, name: from_warehouse },
        to_warehouse: { id: 0, name: to_warehouse },
        transfer_items: [],
        created_datetime: lastMessage.timestamp,
        modified_datetime: lastMessage.timestamp,
        requested_at: lastMessage.timestamp,
        sent_at: null,
        received_at: null,
        order: null,
        order_payment_method: null,
        executor: {
          id: 0,
          first_name: '',
          last_name: '',
          username: wsUser.username,
        },
      })
      return
    }

    // ── transfer.approved ─────────────────────────────────────────────────────
    if (isTransferApprovedEvent(lastMessage)) {
      const {
        id,
        user: wsUser,
        from_warehouse,
        to_warehouse,
        status,
        total_price,
        comment,
        target_admin_ids,
      } = lastMessage.data

      if (user?.id !== undefined && !target_admin_ids.includes(user.id)) return

      if (me?.notifications_enabled !== false) {
        const sound = resolveSound(lastMessage.type)
        if (sound) playSound(sound)

        const viewLabel = t('notifications.view')
        toast({
          variant: 'info',
          title: t('notifications.transfer_approved.title'),
          description: `#${id} — ${from_warehouse} → ${to_warehouse}`,
          duration: 6000,
          action: (
            <ToastAction
              altText={viewLabel}
              onClick={() =>
                navigate(`/products-actions/product-transfer/${id}`)
              }
            >
              {viewLabel}
            </ToastAction>
          ),
        })
      }

      queryClient.setQueryData<Paginated<ApiNotification>>(
        ['notifications'],
        (old) => {
          if (!old) return old
          if (
            old.results.some(
              (n) =>
                n.object_id === id &&
                n.notification_type === 'transfer_approved' &&
                n.id > 0
            )
          )
            return old
          return {
            ...old,
            count: old.count + 1,
            results: [
              {
                id: -Date.now(),
                object_id: id,
                notification_type: 'transfer_approved',
                is_read: false,
                read_by: null,
                warehouse: 0,
                created_datetime: lastMessage.timestamp,
              },
              ...old.results,
            ].slice(0, 20),
          }
        }
      )

      upsertTransferInCache(queryClient, {
        id,
        status,
        comment,
        document_price: total_price,
        from_warehouse: { id: 0, name: from_warehouse },
        to_warehouse: { id: 0, name: to_warehouse },
        transfer_items: [],
        created_datetime: lastMessage.timestamp,
        modified_datetime: lastMessage.timestamp,
        requested_at: null,
        sent_at: null,
        received_at: null,
        order: null,
        order_payment_method: null,
        executor: {
          id: 0,
          first_name: '',
          last_name: '',
          username: wsUser.username,
        },
      })
      return
    }

    // ── transfer.cancelled ────────────────────────────────────────────────────
    if (isTransferCancelledEvent(lastMessage)) {
      const {
        id,
        user: wsUser,
        from_warehouse,
        to_warehouse,
        status,
        total_price,
        comment,
        target_admin_ids,
      } = lastMessage.data

      if (user?.id !== undefined && !target_admin_ids.includes(user.id)) return

      if (me?.notifications_enabled !== false) {
        const sound = resolveSound(lastMessage.type)
        if (sound) playSound(sound)

        const viewLabel = t('notifications.view')
        toast({
          variant: 'info',
          title: t('notifications.transfer_cancelled.title'),
          description: `#${id} — ${from_warehouse} → ${to_warehouse}`,
          duration: 6000,
          action: (
            <ToastAction
              altText={viewLabel}
              onClick={() =>
                navigate(`/products-actions/product-transfer/${id}`)
              }
            >
              {viewLabel}
            </ToastAction>
          ),
        })
      }

      queryClient.setQueryData<Paginated<ApiNotification>>(
        ['notifications'],
        (old) => {
          if (!old) return old
          if (
            old.results.some(
              (n) =>
                n.object_id === id &&
                n.notification_type === 'transfer_cancelled' &&
                n.id > 0
            )
          )
            return old
          return {
            ...old,
            count: old.count + 1,
            results: [
              {
                id: -Date.now(),
                object_id: id,
                notification_type: 'transfer_cancelled',
                is_read: false,
                read_by: null,
                warehouse: 0,
                created_datetime: lastMessage.timestamp,
              },
              ...old.results,
            ].slice(0, 20),
          }
        }
      )

      upsertTransferInCache(queryClient, {
        id,
        status,
        comment,
        document_price: total_price,
        from_warehouse: { id: 0, name: from_warehouse },
        to_warehouse: { id: 0, name: to_warehouse },
        transfer_items: [],
        created_datetime: lastMessage.timestamp,
        modified_datetime: lastMessage.timestamp,
        requested_at: null,
        sent_at: null,
        received_at: null,
        order: null,
        order_payment_method: null,
        executor: {
          id: 0,
          first_name: '',
          last_name: '',
          username: wsUser.username,
        },
      })
      return
    }

    // ── sales_window.* (auto return/cancel warnings + actions) ─────────────────
    if (isSalesWindowNotificationEvent(lastMessage)) {
      const {
        id,
        warehouse_id,
        notification_type,
        deadline,
        target_admin_ids,
      } = lastMessage.data

      if (user?.id !== undefined && !target_admin_ids.includes(user.id)) return

      if (
        isSalesWindowNotificationType(notification_type) &&
        me?.notifications_enabled !== false
      ) {
        const sound = resolveSound(lastMessage.type)
        if (sound) playSound(sound)

        const { title, message } = getSalesWindowNotificationText(
          t,
          notification_type,
          id,
          deadline,
          Date.now()
        )
        const link = salesWindowLink(id)
        const viewLabel = t('notifications.view')

        toast({
          variant: 'info',
          title,
          description: message,
          duration: 6000,
          action: (
            <ToastAction altText={viewLabel} onClick={() => navigate(link)}>
              {viewLabel}
            </ToastAction>
          ),
        })
      }

      queryClient.setQueryData<Paginated<ApiNotification>>(
        ['notifications'],
        (old) => {
          if (!old) return old
          if (
            old.results.some(
              (n) =>
                n.object_id === id &&
                n.notification_type === notification_type &&
                n.id > 0
            )
          )
            return old
          return {
            ...old,
            count: old.count + 1,
            results: [
              {
                id: -Date.now(),
                object_id: id,
                notification_type,
                is_read: false,
                read_by: null,
                warehouse: warehouse_id,
                created_datetime: lastMessage.timestamp,
                deadline,
              },
              ...old.results,
            ].slice(0, 20),
          }
        }
      )

      if (
        notification_type === 'sales_window_auto_returned' ||
        notification_type === 'sales_window_auto_cancelled'
      ) {
        queryClient.invalidateQueries({ queryKey: ['sales-window-list'] })
        queryClient.invalidateQueries({
          queryKey: ['sales-window-detail', String(id)],
        })
      }

      return
    }

    // ── warehouse.debt_limit_consumed ─────────────────────────────────────────
    if (isWarehouseDebtLimitConsumedEvent(lastMessage)) {
      const { target_admin_ids, notification_type, ...payload } =
        lastMessage.data
      const { order_id, warehouse_id } = payload

      if (user?.id !== undefined && !target_admin_ids.includes(user.id)) return

      if (me?.notifications_enabled !== false) {
        const sound = resolveSound(lastMessage.type)
        if (sound) playSound(sound)

        const { title, message } = getWarehouseDebtLimitNotificationText(
          t,
          payload
        )
        const link = warehouseDebtLimitLink(warehouse_id)
        const viewLabel = t('notifications.view')

        toast({
          variant: 'info',
          title,
          description: message,
          duration: 6000,
          action: (
            <ToastAction altText={viewLabel} onClick={() => navigate(link)}>
              {viewLabel}
            </ToastAction>
          ),
        })
      }

      queryClient.setQueryData<Paginated<ApiNotification>>(
        ['notifications'],
        (old) => {
          if (!old) return old
          if (
            old.results.some(
              (n) =>
                n.object_id === order_id &&
                n.notification_type === notification_type &&
                n.id > 0
            )
          )
            return old
          return {
            ...old,
            count: old.count + 1,
            results: [
              {
                id: -Date.now(),
                object_id: order_id,
                notification_type,
                is_read: false,
                read_by: null,
                warehouse: warehouse_id,
                created_datetime: lastMessage.timestamp,
                payload,
              },
              ...old.results,
            ].slice(0, 20),
          }
        }
      )
      return
    }

    // Chat events are handled in ChatPage and useChatSessionWebSocket
    if (isChatSessionCreatedEvent(lastMessage)) return
    if (isChatMessageCreatedEvent(lastMessage)) return
    if (isChatSessionClosedEvent(lastMessage)) return

    console.warn('[WS] Unknown event type:', JSON.stringify(lastMessage))
  }, [
    lastMessage,
    navigate,
    queryClient,
    t,
    user?.id,
    me?.notifications_enabled,
  ])
}
